import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateRange, MatCalendarHeader, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { DateRangeHeaderComponent } from 'app/shared/date-range-picker/date-range-header/date-range-header.component';
import { TimePeriodPanelComponent } from 'app/shared/date-range-picker/time-period-panel/time-period-panel.component';
import { CustomPreset } from 'app/shared/tilled-filter/inputs/filter-date-range/filter-range-panel/filter-range-panel.component';
import moment from 'moment';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    CommonModule,
    TimePeriodPanelComponent,
  ],
})
export class DateRangePickerComponent implements OnInit {
  @Input() formControl: FormControl;
  @Input() initialStart: Date;
  @Input() initialEnd: Date;
  @Input() maxDaysInRange: number;
  @Output() setDateRange: EventEmitter<DatePickerChangedEvent> = new EventEmitter();

  public selectedRangeValue: DateRange<Date> | undefined;
  public endDate: Date = null;
  public startDate: Date = null;
  public excludedRanges: CustomPreset[];
  readonly headerComponent = DateRangeHeaderComponent as unknown as MatCalendarHeader<Date>;

  private dateChanged = new Subject<DatePickerChangedEvent>();

  constructor() {}

  ngOnInit(): void {
    const dateChangedDebounced = this.dateChanged.pipe(debounceTime(50));
    dateChangedDebounced.subscribe((event: DatePickerChangedEvent) => {
      this.setDateRange.emit(event);
    });

    if (this.maxDaysInRange) {
      this.excludedRanges = this.getExcludedRanges();
    }

    if (this.initialStart && this.initialEnd) {
      const rangeIsValid = this.checkDaysInRange(this.initialStart, this.initialEnd);
      if (!rangeIsValid) {
        return;
      }
      this.selectedRangeValue = new DateRange<Date>(this.initialStart, this.initialEnd);
      this.formControl.setValue(this.selectedRangeValue);
    }
  }

  selectedChange(m: any): void {
    if (!this.selectedRangeValue?.start || this.selectedRangeValue?.end) {
      this.selectedRangeValue = new DateRange<Date>(m, null);
    } else {
      const start = this.selectedRangeValue.start;
      const end = m;
      end.setHours(23, 59, 59);
      const rangeIsValid = this.checkDaysInRange(start, end);
      if (!rangeIsValid) {
        return;
      }
      if (end < start) {
        this.selectedRangeValue = new DateRange<Date>(end, start);
      } else {
        this.selectedRangeValue = new DateRange<Date>(start, end);
      }
    }
    if (this.maxDaysInRange) {
      this.endDate = !this.selectedRangeValue?.end ? moment(m).add(this.maxDaysInRange, 'days').toDate() : null;
      this.startDate = !this.selectedRangeValue?.start
        ? moment(m).subtract(this.maxDaysInRange, 'days').toDate()
        : null;
    }
    this.formControl.setValue(this.selectedRangeValue);
  }

  setRange(start: Date, end: Date): void {
    const rangeIsValid = this.checkDaysInRange(start, end);
    if (!rangeIsValid) {
      return;
    }
    this.selectedRangeValue = new DateRange<Date>(start, end);
    this.formControl.setValue(this.selectedRangeValue);
  }

  clearDateRange(): void {
    this.selectedRangeValue = null;
    this.formControl.setValue(null);
  }

  checkDaysInRange(start: Date, end: Date): boolean {
    if (!start || !end || !this.maxDaysInRange) {
      return true;
    }
    const daysInRange = Math.abs(moment(start).diff(moment(end), 'days'));

    return daysInRange <= this.maxDaysInRange;
  }

  getExcludedRanges(): CustomPreset[] {
    const excludedRanges = [];
    const month = 30;
    const year = 365;

    if (this.maxDaysInRange < year) {
      excludedRanges.push('this year');
      excludedRanges.push('last year');
    }
    if (this.maxDaysInRange < month) {
      excludedRanges.push('this month');
      excludedRanges.push('last month');
    }
    // For `(this.maxDaysInRange < week)` We hide the panel entirely if the maxDaysInRange is not >= 7, so it is not needed.
    return excludedRanges;
  }

  close(): void {
    const event: DatePickerChangedEvent = {
      startDate: this.selectedRangeValue.start,
      endDate: this.selectedRangeValue.end,
    };
    this.dateChanged.next(event);
  }
}

export interface DatePickerChangedEvent {
  startDate?: Date;
  endDate?: Date;
}
